import { useState } from 'react'

import { DayCalendar } from '~/components/day-calendar'
import { ClockIcon } from '~/components/icons'
import { TimePicker } from '~/components/time-picker'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import type { NutritionData } from '~/data/nutrition'
import { dayStart, formatDayLabel } from '~/lib/dates'

/**
 * When what is logged will land — the day and the time, on one pill, the way the phone's
 * logging pill says it. Pressing it opens the diary's own calendar for the day, and under it
 * the field the time is typed in, so a meal logged late is put where it was eaten without
 * leaving the page. Picking a day puts the popover away; typing a time does not, a time being
 * typed a digit at a time.
 */
export function WhenPill({
  day,
  time,
  data,
  onDay,
  onTime,
}: {
  day: string
  time: string
  /** The diary, for the calendar's rings; null draws the days bare. */
  data: NutritionData | null
  onDay: (next: string) => void
  onTime: (next: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Logging for ${formatDayLabel(dayStart(day))} at ${time}`}
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-surface-raised py-1.5 pr-3 pl-3 text-xs ring-1 ring-white/10 transition-colors hover:bg-hover data-[state=open]:bg-hover"
        >
          <ClockIcon aria-hidden className="size-4 text-ink-muted" />
          <span>
            {formatDayLabel(dayStart(day))} · {time}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label="When"
        side="top"
        align="start"
        sideOffset={8}
        className="w-auto p-3"
      >
        <div className="flex flex-col gap-3">
          <DayCalendar
            key={day}
            day={day}
            data={data ?? { days: [], incompleteDays: [], goals: [], weeklyCheckinOn: null }}
            trainedDays={new Set()}
            incomplete={false}
            marking={false}
            withMark={false}
            onPick={(next) => {
              onDay(next)
              setOpen(false)
            }}
            onIncomplete={() => undefined}
          />
          <TimePicker value={time} onValue={onTime} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
